import React, { useState } from "react";
import moment from "moment";
import PropTypes from "prop-types";
import { trackActionActivity } from "@src/utils/userActivityTracker";
import { AppUtils, SignolUtils } from "@src/utils";
import { useDCContext } from "@src/context";
import CardContainer from "@src/components/cards/CardContainer";
import HorizontalLayout from "@src/components/cards/HorizontalLayout";
import { Modal, Icon } from "@src/components/atoms";
import { InAppNotificationViewModal } from "./InAppNotificationViewModal";

const NotificationCard = (props) => {
  const { notification, user, onRead } = props;
  const { getDynamicReplacement, existsDynamicReplacements } = useDCContext();

  const [displayTemplate, setDisplayTemplate] = useState(false);

  const notificationTitle = existsDynamicReplacements(notification.title)
    ? getDynamicReplacement(
        notification.title,
        {
          PERIOD_LABEL: moment.utc(notification.createdAt).format("MMMM"),
          LASTNAME: user?.lastname || "",
          ROLE_ALIAS: user?.metadata?.role_alias || "",
        },
        notification.title,
        true,
      )
    : notification.title;

  const actionOpenDetails = () => {
    setDisplayTemplate(true);
    trackActionActivity("NOTIFICATIONS_READ", {
      notificationId: notification.id,
    });
    !!onRead && onRead(notification.id);
  };
  const actionCloseTemplatePreview = () => {
    setDisplayTemplate(false);
  };

  const openPDF = (content) => {
    if (!content.startsWith("data:")) {
      console.warn("Invalid attachment format:", content);
      return;
    }
    const iFrameElement = `<iframe src="${content}" frameborder="0" style="border:0; top:0px; left:0px; bottom:0px; right:0px; width:100%; height:100%;" allowfullscreen></iframe>`;
    const previewWindow = window.open();
    previewWindow.document.write(iFrameElement);
    previewWindow.document.title = "Signol: View Attachment";
  };

  const icon = () => {
    let iconName = "NotificationSupport";
    if (notification.template.includes("MILESTONE")) {
      iconName = "NotificationMilestone";
    } else if (
      notification.template.includes("END_PERIOD") ||
      notification.template.includes("YEAR_END") ||
      notification.template.includes("MIDMONTHLY")
    ) {
      iconName = "NotificationReport";
    } else if (
      notification.template.includes("NEW_BEHAVIOUR") ||
      notification.template.includes("NEWUSER") ||
      notification.template.includes("WELCOME") ||
      notification.template.includes("REMINDER") ||
      notification.template.includes("SPECIAL") ||
      notification.template.includes("CHASER") ||
      notification.template.includes("KICKOFF")
    ) {
      iconName = "NotificationNew";
    }
    return iconName;
  };

  const isDownloadPage =
    !!notification.protected && !!notification.downloadLink;

  return (
    <>
      <CardContainer
        bgColor={isDownloadPage ? "gray" : "white"}
        padding={0}
        onClick={() => {
          // setDisplayTemplate(true);
          actionOpenDetails();
        }}
      >
        <HorizontalLayout ratio={[!notification.isRead ? 4 : 0, "flex-1", 12]}>
          {/* child 1 */}
          <div className="text-white p-2 rounded-full pb-6">
            {!notification.isRead && !isDownloadPage && (
              <Icon
                name={"circle"}
                textColor={"primary"}
                backgroundColor={"primary"}
              />
            )}
          </div>
          {/* child 2 */}
          <div className="px-2 space-y-3">
            <div
              className={`font-bold ${notification.isRead ? "text-neutral-500" : ""}`}
            >
              {notificationTitle}
            </div>
            {notification.attachments &&
              notification.attachments.length > 0 && (
                <div>
                  {notification.attachments.map((content, idx) => (
                    <div
                      key={idx}
                      className="text-sm inline-flex hover:scale-[1.01] active:translate-y-px transition-transform"
                      onClick={(e) => {
                        e.stopPropagation(); //Don't also open the HTML email in the Modal
                        openPDF(content);
                        //Since event propagation is stopped, track manually
                        trackActionActivity("NOTIFICATIONS_OPEN_ATTACHMENT", {
                          notificationId: notification.id,
                        });
                      }}
                    >
                      <Icon name="attachment" size="lg" centered={false} />
                      <span className="text-sm text-primary-500 underline self-center">
                        attachment-{idx + 1}.pdf
                      </span>
                    </div>
                  ))}
                </div>
              )}
            <div className="text-xs text-gray-500 tracking-wide">
              {moment.utc(notification.createdAt).fromNow()}
              {" | "}
              {moment.utc(notification.createdAt).format("MMM DD, YYYY HH:mm")}
            </div>
          </div>
          {/* child 3 */}
          <div className=" text-white flex h-full w-full">
            <Icon name={icon()} size={"lg"} padding={false} />
          </div>
        </HorizontalLayout>
      </CardContainer>
      <InAppNotificationViewModal
        notificationHtml={notification.content}
        open={displayTemplate}
        handleClose={actionCloseTemplatePreview}
      />
    </>
  );
};

export const notificationCardControls = {
  notification: {
    propType: PropTypes.object,
    description: "Notification data object",
    controlType: "object",
  },
  user: {
    propType: PropTypes.object,
    description: "User info",
    controlType: "object",
  },
  getDynamicContent: {
    propType: PropTypes.func,
    description: "Function to get dynamic content",
    disable: true,
  },
};

NotificationCard.propTypes = AppUtils.getPropTypeFromControls(
  notificationCardControls,
);

export default NotificationCard;
